/**
 * Handles events for a select box.
 *
 *   - Backspace: Executes `data-on-clear-selection` LiveView.JS command when cursor is at the start of input
 *   - Escape: Blurs the input and executes the `data-on-focus-out` LiveView.JS command
 *   - Focus out: Executes the `data-on-focus-out` LiveView.JS command
 */

import { Hook, makeHook } from './Hook'

export default makeHook(
  class SelectEvents extends Hook {
    mounted() {
      this.handleFocusOut = this.handleFocusOut.bind(this)
      this.handleKeyDown = this.handleKeyDown.bind(this)

      this.el.addEventListener('focusout', this.handleFocusOut)
      this.el.addEventListener('keydown', this.handleKeyDown)
    }

    destroyed() {
      this.el.removeEventListener('focusout', this.handleFocusOut)
      this.el.removeEventListener('keydown', this.handleKeyDown)
    }

    handleFocusOut(event: FocusEvent) {
      const relatedTarget = event.relatedTarget as HTMLElement

      // relatedTarget will be null when changing browser tabs
      if (relatedTarget && !relatedTarget.closest('[role="option"]')) {
        this.pushFocusOut()
      }
    }

    handleKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        case 'Backspace':
          const onClearSelection = this.el.getAttribute('data-on-clear-selection')

          if ((this.el as HTMLInputElement).selectionStart === 0 && onClearSelection) {
            this.liveSocket.execJS(this.el, onClearSelection)
          }

          break

        case 'Escape':
          if (document.activeElement === this.el) {
            event.stopPropagation()
            this.el.blur()
            this.pushFocusOut()
          }

          break
      }
    }

    pushFocusOut() {
      const onFocusOut = this.el.getAttribute('data-on-focus-out')

      if (onFocusOut) {
        this.liveSocket.execJS(this.el, onFocusOut)
      }
    }
  }
)
